import React, { useContext } from "react";
import { View, StyleSheet } from "react-native";
import {
  PrimaryButton,
  SecondaryButton,
  NormalText,
  TitleText,
} from "../../../common/native";
import { SessionContext } from "../../../../contexts";
import { mixpanel } from "../../../../utils/mixpanel";
import { TClinicUsageResponse } from "../../../../services/api";

type UpgradePromptModalProps = {
  onClose: () => void;
  usageStats?: TClinicUsageResponse | null;
  variant?: "default" | "freeSimulationLimit";
};

const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  onClose,
  usageStats,
  variant = "default",
}) => {
  const session = useContext(SessionContext)?.session;
  const isFreeSimulationLimit = variant === "freeSimulationLimit";

  // const leadCount = usageStats?.patients_by_type?.["LEAD"] ?? 0;

  const highlights: { label: string; value: number }[] = [];

  // if (leadCount > 5)
  //   highlights.push({ label: "leads captured", value: leadCount });
  if (usageStats) {
    if ((usageStats?.total_form_submissions ?? 0) > 5)
      highlights.push({
        label: "leads captured",
        value: usageStats.total_form_submissions,
      });
    if ((usageStats?.total_side_by_side_images ?? 0) > 5)
      highlights.push({
        label: "before/after comparisons",
        value: usageStats.total_side_by_side_images,
      });
    if ((usageStats?.total_simulations ?? 0) > 5)
      highlights.push({
        label: "AI simulations shown",
        value: usageStats.total_simulations,
      });
    if ((usageStats?.total_audio_files ?? 0) > 5)
      highlights.push({
        label: "voice notes recorded",
        value: usageStats.total_audio_files,
      });
  }

  const freeLimitBenefits = [
    "Keep running high-converting simulations in every consult.",
    "Unlock higher usage for your team as your clinic grows.",
    "Avoid interruptions during live patient conversations.",
  ];

  return (
    <View style={styles.container}>
      <TitleText
        text={
          isFreeSimulationLimit
            ? "You've Reached Your FREE Simulation Limit"
            : "🔥 Your Leads Are Engaging"
        }
        style={styles.header}
        fontWeight="600"
      />
      <NormalText
        text={
          isFreeSimulationLimit
            ? "Upgrade your plan to continue using ClinicOS simulations without interruption."
            : "You're on the FREE plan — but your leads are active and converting. Here's what ClinicOS is doing already:"
        }
        style={styles.body}
        numberOfLines={isFreeSimulationLimit ? 3 : 4}
      />
      {isFreeSimulationLimit ? (
        <View style={styles.limitVisualContainer}>
          <View style={styles.limitBadge}>
            <NormalText
              text="0 free simulations remaining"
              style={styles.limitBadgeText}
            />
          </View>
          <View style={styles.benefitList}>
            {freeLimitBenefits.map((benefit, idx) => (
              <View key={idx} style={styles.benefitRow}>
                <View style={styles.benefitDot} />
                <NormalText text={benefit} style={styles.benefitText} />
              </View>
            ))}
          </View>
        </View>
      ) : (
        <View style={styles.statsContainer}>
          {highlights.map(({ label, value }, idx) => (
            <View key={idx} style={styles.statRow}>
              <TitleText
                text={String(value)}
                fontWeight="700"
                style={styles.statValue}
              />
              <NormalText text={label} style={styles.statLabel} />
            </View>
          ))}
        </View>
      )}

      <NormalText
        text={
          isFreeSimulationLimit
            ? "Upgrade now to restore simulation access and keep your workflow moving."
            : "Upgrade now to automate even more and capture every opportunity."
        }
        style={styles.footnote}
        numberOfLines={2}
      />
      <PrimaryButton
        text={isFreeSimulationLimit ? "Upgrade Plan" : "Upgrade Now"}
        onPress={() => {
          mixpanel.track("Upgrade Prompt Clicked", {
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
            source: isFreeSimulationLimit
              ? "free_simulation_limit_modal"
              : "engagement_prompt_modal",
          });
          window.location.href = "/upgrade";
        }}
      />
      <SecondaryButton
        text="Maybe Later"
        style={{ marginTop: 12 }}
        onPress={onClose}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    padding: 24,
    borderRadius: 16,
    maxWidth: 500,
    alignItems: "center",
  },
  header: {
    textAlign: "center",
    marginBottom: 16,
  },
  body: {
    textAlign: "center",
    marginBottom: 20,
  },
  statsContainer: {
    width: "100%",
    marginBottom: 20,
  },
  statRow: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "center",
    marginBottom: 8,
  },
  statValue: {
    fontSize: 24,
    color: "#00B7F4",
    marginRight: 8,
  },
  statLabel: {
    fontSize: 16,
    color: "#333",
  },
  footnote: {
    marginBottom: 20,
    textAlign: "center",
    color: "#444",
  },
  limitVisualContainer: {
    width: "100%",
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#D5EDF7",
    borderRadius: 16,
    backgroundColor: "#F3FBFF",
    padding: 16,
    gap: 14,
  },
  limitBadge: {
    alignSelf: "center",
    backgroundColor: "#A1E3CB",
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  limitBadgeText: {
    color: "#111",
    fontFamily: "Urbanist600",
    fontSize: 14,
  },
  benefitList: {
    gap: 10,
  },
  benefitRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  benefitDot: {
    width: 8,
    height: 8,
    marginTop: 6,
    borderRadius: 999,
    backgroundColor: "#00B7F4",
  },
  benefitText: {
    flex: 1,
    fontSize: 14,
    color: "#1D252D",
  },
});

export default UpgradePromptModal;
